/**
 * Utility functions for converting NodeDto arrays to InternedNodeDto arrays.
 *
 * String interning replaces duplicate string values with indices pointing
 * to a shared StringStorage, significantly reducing memory usage when
 * loading large node arrays with many duplicate string values.
 */

import type { NodeDto } from '@memorypack/NodeDto';
import { InternedNodeDto } from './InternedNodeDto';
import { StringStorage } from './StringStorage';

/**
 * Configuration options for string interning behavior.
 */
export interface InterningConfig {
  /**
   * When true, string interning is enabled for memory optimization.
   * When false, strings are stored directly (no deduplication).
   * @default true
   */
  enabled: boolean;
}

/**
 * Gets whether string interning is currently enabled.
 */
export function isInterningEnabled(): boolean {
  return InternedNodeDto.interningEnabled;
}

/**
 * Enables or disables string interning globally.
 * Call this before creating InternedNodeDto instances.
 * 
 * @param enabled - Whether to enable string interning
 */
export function setInterningEnabled(enabled: boolean): void {
  InternedNodeDto.setInterningEnabled(enabled);
}

/**
 * Converts an array of NodeDto objects to InternedNodeDto objects,
 * interning string fields to reduce memory usage (if interning is enabled).
 *
 * The storage must be set on InternedNodeDto before calling this function
 * if interning is enabled:
 * ```typescript
 * const storage = new StringStorage();
 * InternedNodeDto.setStorage(storage);
 * const internedNodes = internNodeArray(nodes);
 * ```
 *
 * @param nodes - Array of NodeDto objects to convert
 * @returns Array of InternedNodeDto objects with interned strings
 */
export function internNodeArray(nodes: readonly NodeDto[]): InternedNodeDto[] {
  const result: InternedNodeDto[] = new Array(nodes.length);

  for (let i = 0; i < nodes.length; i++) {
    result[i] = internNode(nodes[i]);
  }

  return result;
}

/**
 * Converts a single NodeDto to an InternedNodeDto.
 *
 * @param node - The NodeDto to convert
 * @returns A new InternedNodeDto with interned strings
 */
export function internNode(node: NodeDto): InternedNodeDto {
  const interned = new InternedNodeDto();

  // Copy non-interned fields directly
  interned.nodeId = node.nodeId;
  interned.nodePath = node.nodePath;
  interned.nodeType = node.nodeType;
  interned.modelId = node.modelId;
  interned.cadModelCategoryId = node.cadModelCategoryId;
  interned.isStructuralMaterial = node.isStructuralMaterial;
  interned.scopeId = node.scopeId;
  interned.componentCount = node.componentCount;
  interned.quantity = node.quantity;
  interned.length = node.length;
  interned.area = node.area;
  interned.volume = node.volume;
  interned.thickness = node.thickness;
  interned.originalQuantity = node.originalQuantity;
  interned.originalLength = node.originalLength;
  interned.originalArea = node.originalArea;
  interned.originalVolume = node.originalVolume;
  interned.originalThickness = node.originalThickness;
  interned.multiplierValue = node.multiplierValue;
  interned.carbonImpactValue = node.carbonImpactValue;
  interned.hasChildCarbonImpactOverride = node.hasChildCarbonImpactOverride;
  interned.systemId = node.systemId;
  interned.subSystemId = node.subSystemId;
  interned.modelParameters = node.modelParameters;

  // Copy interned fields - setters automatically intern the strings
  interned.cadId = node.cadId;
  interned.cadFamily = node.cadFamily;
  interned.cadName = node.cadName;
  interned.cadMaterial = node.cadMaterial;
  interned.cadReferenceId = node.cadReferenceId;
  interned.omniClassNumber = node.omniClassNumber;
  interned.omniClassTitle = node.omniClassTitle;
  interned.uniFormatNumber = node.uniFormatNumber;
  interned.uniFormatTitle = node.uniFormatTitle;
  interned.masterFormatNumber = node.masterFormatNumber;
  interned.masterFormatTitle = node.masterFormatTitle;
  interned.multiplierInput = node.multiplierInput;
  interned.notes = node.notes;
  interned.carbonImpactInput = node.carbonImpactInput;
  interned.carbonImpactNotes = node.carbonImpactNotes;
  interned.freeText1 = node.freeText1;
  interned.freeText2 = node.freeText2;
  interned.freeText3 = node.freeText3;

  return interned;
}

/**
 * Creates a new StringStorage and sets it up for InternedNodeDto.
 * Returns the storage instance for later cleanup.
 *
 * @returns A new StringStorage instance that has been set on InternedNodeDto
 */
export function createAndSetStorage(): StringStorage {
  const storage = new StringStorage();
  InternedNodeDto.setStorage(storage);
  return storage;
}

/**
 * Cleans up the string interning resources.
 * Call this after processing is complete to free memory.
 *
 * @param storage - The StringStorage instance to clear
 */
export function cleanupInterning(storage: StringStorage): void {
  storage.clear();
  InternedNodeDto.clearStorage();
}
